import { useEffect, useMemo, useState } from 'react'
import {
  vehicleFleetCreateVehicle,
  vehicleFleetDeactivateVehicle,
  vehicleFleetDeleteVehicle,
  vehicleFleetLoadDrivers,
  vehicleFleetLoadVehicles,
  vehicleFleetUpdateVehicle,
  type VehicleFleetMember,
  type VehicleFleetVehicle,
} from '../../lib/apiPhpAdapter'
import { comparePersonNames } from '../../utils/personNameSort'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const emptyForm = { name: '', model: '', plate_number: '', assigned_driver_user_id: '' }

export function VehicleFleetVehiclesPanel({ showAlert }: Props) {
  const [vehicles, setVehicles] = useState<VehicleFleetVehicle[]>([])
  const [drivers, setDrivers] = useState<VehicleFleetMember[]>([])
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const driverOptions = useMemo(() => {
    const active = drivers.filter((d) => !!d.active && d.user_id)
    return [...active].sort((a, b) =>
      comparePersonNames(
        { name: a.display_name || a.user_name || '' },
        { name: b.display_name || b.user_name || '' },
      ),
    )
  }, [drivers])

  const reload = async () => {
    const [list, drv] = await Promise.all([vehicleFleetLoadVehicles(), vehicleFleetLoadDrivers()])
    setVehicles(Array.isArray(list) ? list : [])
    setDrivers(Array.isArray(drv) ? drv : [])
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [list, drv] = await Promise.all([
          vehicleFleetLoadVehicles(),
          vehicleFleetLoadDrivers(),
        ])
        if (!cancelled) {
          setVehicles(Array.isArray(list) ? list : [])
          setDrivers(Array.isArray(drv) ? drv : [])
        }
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst vozidla', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showAlert])

  const startEdit = (v: VehicleFleetVehicle) => {
    setEditId(v.id)
    setForm({
      name: v.name,
      model: v.model,
      plate_number: v.plate_number,
      assigned_driver_user_id: v.assigned_driver_user_id || '',
    })
  }

  const resetForm = () => {
    setEditId(null)
    setForm(emptyForm)
  }

  const assignedPayload = form.assigned_driver_user_id.trim()
    ? form.assigned_driver_user_id.trim()
    : null

  return (
    <section
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '1.25rem 1.4rem',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        maxWidth: 720,
        marginBottom: 16,
      }}
    >
      <h2 style={{ marginTop: 0 }}>Vozidla autoparku</h2>
      <p style={{ marginBottom: 14, color: '#64748b', lineHeight: 1.45 }}>
        Přidejte vozidlo a volitelně přiřaďte <strong>stálého řidiče</strong>. Ten vozidlo běžně
        používá; pokud potřebuje mít jistotu na konkrétní termín, vytvoří rezervaci. Ostatní řidiči
        mohou vozidlo rezervovat jen když na termín není rezervace.
      </p>

      <div style={{ display: 'grid', gap: 8, marginBottom: 16, maxWidth: 480 }}>
        <label style={{ display: 'grid', gap: 4, fontSize: '0.9rem' }}>
          Název
          <input
            value={form.name}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="např. Dodávka 1"
            style={{ padding: '0.45rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: '0.9rem' }}>
          Model
          <input
            value={form.model}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            placeholder="např. VW Transporter"
            style={{ padding: '0.45rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: '0.9rem' }}>
          Číslo (SPZ)
          <input
            value={form.plate_number}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, plate_number: e.target.value.toUpperCase() }))}
            placeholder="např. 1AB 2345"
            style={{ padding: '0.45rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: '0.9rem' }}>
          Stálý řidič
          <select
            value={form.assigned_driver_user_id}
            disabled={busy}
            onChange={(e) => setForm((f) => ({ ...f, assigned_driver_user_id: e.target.value }))}
            style={{ padding: '0.45rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
          >
            <option value="">— Bez stálého řidiče —</option>
            {driverOptions.map((d) => (
              <option key={d.user_id} value={String(d.user_id)}>
                {d.display_name || d.user_name || d.user_id}
              </option>
            ))}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="system-button"
            disabled={busy || !form.name.trim() || !form.model.trim() || !form.plate_number.trim()}
            onClick={() => {
              void (async () => {
                setBusy(true)
                try {
                  if (editId != null) {
                    await vehicleFleetUpdateVehicle(editId, {
                      name: form.name.trim(),
                      model: form.model.trim(),
                      plate_number: form.plate_number.trim(),
                      assigned_driver_user_id: assignedPayload,
                    })
                    showAlert('Vozidlo upraveno', 'success')
                  } else {
                    await vehicleFleetCreateVehicle({
                      name: form.name.trim(),
                      model: form.model.trim(),
                      plate_number: form.plate_number.trim(),
                      assigned_driver_user_id: assignedPayload,
                    })
                    showAlert('Vozidlo přidáno', 'success')
                  }
                  resetForm()
                  await reload()
                } catch (e: unknown) {
                  showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
                } finally {
                  setBusy(false)
                }
              })()
            }}
          >
            {editId != null ? 'Uložit změny' : 'Přidat vozidlo'}
          </button>
          {editId != null ? (
            <button type="button" disabled={busy} onClick={resetForm}>
              Zrušit úpravu
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <p>Načítání…</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {vehicles.map((v) => (
            <li
              key={v.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
                padding: '0.6rem 0',
                borderBottom: '1px solid #e2e8f0',
                opacity: v.active ? 1 : 0.55,
              }}
            >
              <div>
                <strong>{v.name}</strong>
                <div style={{ fontSize: '0.9rem', color: '#475569' }}>
                  {v.model} · {v.plate_number}
                  {!v.active ? ' · neaktivní' : ''}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 2 }}>
                  {v.assigned_driver_name
                    ? `Stálý řidič: ${v.assigned_driver_name}`
                    : 'Bez stálého řidiče'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" disabled={busy} onClick={() => startEdit(v)}>
                  Upravit
                </button>
                {v.active ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void (async () => {
                        setBusy(true)
                        try {
                          await vehicleFleetDeactivateVehicle(v.id)
                          await reload()
                          showAlert('Vozidlo deaktivováno', 'success')
                        } catch (e: unknown) {
                          showAlert(e instanceof Error ? e.message : 'Deaktivace selhala', 'error')
                        } finally {
                          setBusy(false)
                        }
                      })()
                    }}
                  >
                    Deaktivovat
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void (async () => {
                        setBusy(true)
                        try {
                          await vehicleFleetUpdateVehicle(v.id, { active: true })
                          await reload()
                          showAlert('Vozidlo aktivováno', 'success')
                        } catch (e: unknown) {
                          showAlert(e instanceof Error ? e.message : 'Aktivace selhala', 'error')
                        } finally {
                          setBusy(false)
                        }
                      })()
                    }}
                  >
                    Aktivovat
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  style={{ color: '#b91c1c' }}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Trvale smazat vozidlo „${v.name}“ (${v.plate_number})? Nelze, pokud má aktivní rezervace.`,
                      )
                    ) {
                      return
                    }
                    void (async () => {
                      setBusy(true)
                      try {
                        await vehicleFleetDeleteVehicle(v.id)
                        if (editId === v.id) resetForm()
                        await reload()
                        showAlert('Vozidlo smazáno', 'success')
                      } catch (e: unknown) {
                        showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
                      } finally {
                        setBusy(false)
                      }
                    })()
                  }}
                >
                  Smazat
                </button>
              </div>
            </li>
          ))}
          {vehicles.length === 0 ? (
            <li style={{ color: '#64748b', padding: '0.5rem 0' }}>Zatím žádná vozidla.</li>
          ) : null}
        </ul>
      )}
    </section>
  )
}
